import { Country, FilterKeys, Filters, Option } from '@utils/pageConfig/Types';
import { Periodicity } from '@components/time-period-selector/PeriodsTypes';
import { getDefaultValueForDerivablePeriodicity, getPeriodMetaFactory } from '@utils/PeriodUtils';
import { BackendComparisonPeriodTypeMapping, RelativePeriod } from '@components/comparison-period-selector/Types';
import { PerformanceAnalysisParam } from '@data-visualization/performance-analysis-line-chart/Types';
import { ProductDataType, ProductType } from '@pages/product-analyzer/Product';
import { getCollectionsLiveDataParams } from '@utils/collectionUtils/CollectionUtils';
import { ExploreTrendsStackParams } from '@data-visualization/trends-stack-chart/Types';
import { applyCommonFilterParamsToRequest } from '@utils/serviceUtils/RequestFormatter';

export const DefaultProductGroup = 'ALL_PRODUCT_GROUP';

export const paramsSerializer = (params: any) => {
  return Object.entries({ ...params })
    .filter(([_, value]) => value !== '')
    .map(([key, value]) => `${key}=${value}`)
    .join('&');
};

interface RequestParam {
  clientId: number;
  productGroup: Option[] | undefined;
  countries: Country[];
  period: string;
  comparisonPeriod: string;
  periodType: Periodicity;
  saleType: string;
  isSummary: boolean;
}

interface RequestPostParam {
  clientId: number;
  productGroup: Option[] | undefined;
  countries: Country[];
  period: string;
  comparisonPeriod: string;
  periodType: Periodicity;
  saleType: string;
  appliedFilters: Filters[];
  isSummary: boolean;
}

const prepareRequest = ({
  clientId,
  productGroup,
  countries,
  period,
  comparisonPeriod,
  periodType,
  saleType,
  isSummary,
}: RequestParam) => {
  const periodMeta = getPeriodMetaFactory(period, comparisonPeriod, periodType, undefined, true);
  const pg = getPgData(productGroup);

  return {
    clientId: clientId,
    productGroup: pg,
    country: countries.map((country) => country.id),
    period: periodMeta ? periodMeta.currentPeriod.toUpperCase() : '',
    comparisonPeriod: periodMeta ? periodMeta.comparisonPeriod.toUpperCase() : '',
    comparisonPeriodType: BackendComparisonPeriodTypeMapping[comparisonPeriod as RelativePeriod],
    periodType: periodType ? periodType.toUpperCase().replace(/\s/g, '') : '',
    saleType: saleType ? saleType.toUpperCase() : '',
    split: '',
    channel: '',
    region: '',
    city: '',
    brand: '',
    priceband: '',
    segFeat: '',
    feature: '',
    featureValue: '',
    isSummary: isSummary,
  };
};

export const getComparisonPeriodType = (comparisonPeriod: string) => {
  return comparisonPeriod === RelativePeriod.PreviousYearCurrentPeriod ? 'SAME_PERIOD_LAST_YEAR' : 'PREVIOUS_PERIOD';
};

const getPgData = (productGroup: Option[] | undefined): string => {
  return productGroup
    ? productGroup
        .map((pg: { value: any }) => {
          return encodeURIComponent(pg.value);
        })
        .join('&productGroup=')
    : DefaultProductGroup;
};

export const getAbortController = () => {
  return new AbortController();
};

export const generateParamsPOST = ({
  clientId,
  productGroup,
  countries,
  period,
  comparisonPeriod,
  periodType,
  saleType,
  appliedFilters,
  isSummary,
}: RequestPostParam) => {
  const params = prepareRequest({
    clientId,
    productGroup,
    countries,
    period,
    comparisonPeriod,
    periodType,
    saleType,
    isSummary,
  });

  applyParamsBasedOnFilterPOST(params, appliedFilters);
  // Remove empty values in post request body
  return Object.fromEntries(Object.entries(params).filter(([, value]) => value !== ''));
};

export const applyParamsBasedOnFilterPOST = (req: any, appliedFilters: Filters[]) => {
  appliedFilters.forEach((filter) => {
    // >= FilterKeys.ONLINE_OFFLINE due to ONLINE_OFFLINE value is 2 and afterward all are different segment.
    // filter.options.length > 0 to prevent payload inject empty data segment value.
    if (filter.filterKey >= FilterKeys.ONLINE_OFFLINE && filter.options.length > 0) {
      applyCommonFilterParamsToRequest(req, filter);
    }
  });
};

export const generateFinalizedParamsPOST = (
  queryParams: { [k: string]: string | number | boolean | string[] },
  productType: ProductType,
  pgOption: Option[] | undefined,
  country: string | string[] | number | boolean,
) => {
  const productDataType = ProductDataType.PerformanceAnalyzer;

  if (pgOption && pgOption.length !== 0) {
    return {
      ...queryParams,
      productType,
      productDataType,
      productGroup: pgOption?.map((pg) => pg.value),
      country: country,
    };
  }
  return {
    ...queryParams,
    productType,
    productDataType,
    country: country,
  };
};
export const generateParamPA = (param: PerformanceAnalysisParam) => {
  const queryParams = generateParamsPOST({
    clientId: param.clientId,
    productGroup: param.productGroup,
    countries: param.countries,
    period: param.period,
    comparisonPeriod: param.comparisonPeriod,
    periodType: getDefaultValueForDerivablePeriodicity(param.periodType),
    saleType: param.saleType,
    appliedFilters: param.appliedFilters,
    isSummary: param.isSummary,
  });
  const collectionsParam = getCollectionsLiveDataParams(
    !!param.isUsingCollectionLiveData,
    param.periodType,
    param.comparisonPeriod,
  );
  return {
    ...collectionsParam,
    ...queryParams,
    productType: param.productType,
    productDataType: ProductDataType.PerformanceAnalyzer,
    productGroup: param.productGroup.map((pg) => pg.value),
    country: queryParams.country,
    kpi: param.kpi,
    showPerformance: param.segment,
    dataType: param.dataType,
  };
};

export const generateParamExploreTrendsStack = (param: ExploreTrendsStackParams) => {
  const queryParams = generateParamsPOST({
    clientId: param.clientId,
    productGroup: param.productGroup,
    countries: param.countries,
    period: param.period,
    comparisonPeriod: param.comparisonPeriod,
    periodType: getDefaultValueForDerivablePeriodicity(param.periodType),
    saleType: param.saleType,
    appliedFilters: param.appliedFilters,
    isSummary: param.isSummary,
  });
  const collectionsParam = getCollectionsLiveDataParams(
    !!param.isUsingCollectionLiveData,
    param.periodType,
    param.comparisonPeriod,
  );
  return {
    ...collectionsParam,
    ...queryParams,
    productType: param.productType,
    saleFormat: param.saleFormat,
    productDataType: ProductDataType.PerformanceAnalyzer,
    productGroup: param.productGroup.map((pg) => pg.value),
    country: queryParams.country,
    kpi: param.kpi,
    showPerformance: param.segment,
    dataType: param.dataType,
  };
};
